import names from './actionNames';
import axios from 'axios';
import { isString } from 'lodash';

import HttpStatus from 'http-status-codes';
import { getGrpcMessage } from '~/common/grpc/helpers';
import { loadingFinished, loadingStarted } from './progressActions';
import { setItem } from '~/services/localStorage';
import GRPC_USER from '~/common/grpc/UserService';
import GRPC_AUTH from '~/common/grpc/AuthService';
import API from '~/api/endpointsAPI';
import { message } from 'antd';
import {
  setCookie,
  getCookie,
  deleteCookie,
  REDIRECT_TO_COOKIE_KEY,
  TOKEN_COOKIE_KEY
} from '~/services/cookies';

import { RESPONSE_STATUS_FLAG } from '~/common/constants';
const { SUCCESS, ERROR } = RESPONSE_STATUS_FLAG;

const reqOrigins = {
  login: 'loginFlow',
  externalAuthentication: 'externalAuthenticationFlow'
};

// action creators

export const loginSuccess = data => ({
  type: names.LOGIN_SUCCESS,
  data
});

export const loginError = error => ({
  type: names.LOGIN_FAILED,
  error
});

export const clearLoginError = () => ({
  type: names.LOGIN_FAILED_CLEAR_ERROR
});

export const logoutSuccess = message => ({
  type: names.LOGOUT_SUCCESS,
  message
});

// actions

export const msg = {
  connectionError:
    'There is an issue with the network connection. Please check your settings and try again.',
  gatewayError: 'The server didn’t respond in time. Please try again later.',
  defaultError: 'Sorry, some error occured.',
  loginError: 'Unable to perform login.',
  userDeleted: "User isn't exist or deleted",
  wrongUserOrPass: 'Wrong username or password',
  userInactive: 'User disabled',
  useForgotPassword:
    'Your username and password incorrect. Please use "forgot my password" to reset your password if you have forgotten it.',
  lockedAccount:
    'Your account has been locked. Please contact your IT ​administrator to get your account unlocked.',
  useInvatation:
    'Please check your email box for the invite link or contact administrator.',
  userOrganizationInactive:
    'User/Agent ID is INACTIVE. Please contact administrator.',
  userNotRegistered:
    'The user is not registered to use the system. Please contact Service Desk.'
};

export function loginRequest(login, password) {
  return dispatch => {
    return processLoginFlow(dispatch, login, password);
  };
}

function processLoginFlow(dispatch, login, password) {
  dispatch(clearLoginError());
  dispatch(loadingStarted());

  return axios
    .post(API.AUTHENTICATE, { login, password })
    .then(response => {
      setItem('auth', response.data);
      let token = response.data.tokenPair;

      if (
        !isString(token.accessToken) ||
        !isString(token.refreshToken) ||
        !isString(token.login)
      ) {
        const error = new Error(msg.loginError);
        return dispatch(loginError(error));
      } else {
        setCookie(TOKEN_COOKIE_KEY, token.accessToken, true);
        setTimeout(() => {
          const redirectTo = getCookie(REDIRECT_TO_COOKIE_KEY);
          if (redirectTo) {
            deleteCookie(REDIRECT_TO_COOKIE_KEY, true);
            window.location.replace(redirectTo);
          }
        });
      }

      return dispatch(loginSuccess(token));
    })
    .catch(error => {
      const errorObj = selectPropperErrorMsg(error, reqOrigins.login);
      return dispatch(loginError(errorObj));
    })
    .finally(() => {
      return dispatch(loadingFinished());
    });
}

export function logout(message = '') {
  return dispatch => {
    setItem('auth', null);
    setCookie('token', '', true);
    return dispatch(logoutSuccess(message));
  };
}

const selectPropperErrorMsg = (responseError, requestOrigin) => {
  const { response } = responseError;
  const status = response && response.status ? response.status : 0;
  const grpc = getGrpcMessage(response);

  const error = {
    status,
    grpc,
    message: ''
  };

  const userOffline = !navigator.onLine && !response;

  if (userOffline) {
    error.message = msg.connectionError;
  } else if (status === HttpStatus.GATEWAY_TIMEOUT) {
    error.message = msg.gatewayError;
  } else {
    switch (grpc) {
      case GRPC_USER.NOT_FOUND.USER:
        // this code used for few scenarios
        // so msg might be different for other cases
        // like change-passoword for example
        if (requestOrigin === reqOrigins.login) {
          error.message = msg.wrongUserOrPass;
        } else if (requestOrigin === reqOrigins.externalAuthentication) {
          error.message = msg.userNotRegistered;
        } else {
          error.message = msg.defaultError;
        }
        break;
      case GRPC_USER.INVALID_ARGUMENT.GENERAL:
        error.message = msg.wrongUserOrPass;
        break;
      case GRPC_AUTH.UNAUTHENTICATED.NO_USERS:
        error.message = msg.wrongUserOrPass;
        break;
      case GRPC_AUTH.UNAUTHENTICATED.USER_DELETED:
        error.message = msg.userDeleted;
        break;
      case GRPC_AUTH.UNAUTHENTICATED.USER_INACTIVE:
        error.message = msg.userInactive;
        break;
      case GRPC_AUTH.UNAUTHENTICATED.NEED_CAPTCHA:
        error.message = msg.useForgotPassword;
        break;
      case GRPC_AUTH.UNAUTHENTICATED.ACCOUNT_LOCKED:
        error.message = msg.lockedAccount;
        break;
      case GRPC_AUTH.UNAUTHENTICATED.USE_INVITATION:
        error.message = msg.useInvatation;
        break;
      case GRPC_AUTH.UNAUTHENTICATED.USER_ORG_INACTIVE:
        error.message = msg.userOrganizationInactive;
        break;
      default:
        error.message = msg.defaultError;
    }
  }

  return error;
};

export function getExternalAuthMetadata(payload) {
  return dispatch => {
    return processExtAuthMetadata(dispatch, payload);
  };
}

const processExtAuthMetadata = async (dispatch, payload) => {
  const { type: authenticationMethodType } = payload;
  const response = await postRequestHandler(API.EXT_AUTHENTICATE_METADATA, {
    authenticationMethodType
  });

  const { statusFlag, data } = response;
  if (statusFlag === SUCCESS) {
    return data;
  }
};

export const postRequestHandler = async (url, params) => {
  try {
    const result = await axios.post(url, params);
    return { data: result.data, statusFlag: SUCCESS };
  } catch (error) {
    return { error: error, statusFlag: ERROR };
  }
};

export function externalAuthenticate(payload) {
  return dispatch => {
    return processExternalAuthenticate(dispatch, payload);
  };
}

const processExternalAuthenticate = async (dispatch, payload) => {
  const { type: authenticationMethodType, code: token } = payload;
  const response = await postRequestHandler(API.EXT_AUTHENTICATE, {
    authenticationMethodType,
    token
  });

  const { statusFlag, data } = response;
  if (statusFlag === SUCCESS) {
    return processResponse(data);
  }
  processError(response);
  return false;
};

function processResponse(data) {
  setItem('auth', data);
  let token = data.tokenPair;
  if (
    !isString(token.accessToken) ||
    !isString(token.refreshToken) ||
    !isString(token.login)
  ) {
    return false;
  } else {
    setCookie(TOKEN_COOKIE_KEY, token.accessToken, true);
    return true;
  }
}

function processError(response) {
  const { error } = response;
  // eslint-disable-next-line no-unused-vars
  const errorObj = selectPropperErrorMsg(
    error,
    reqOrigins.externalAuthentication
  );
  message.error(errorObj.message);
}
